import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Star } from "lucide-react";
import LeadForm from "../LeadForm";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ children, delay }) => (
  <span className="line-mask">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const orbY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const orbY2 = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden bg-white pt-28 sm:pt-36 pb-20"
      data-testid="hero-section"
    >
      {/* Background orbs */}
      <motion.div
        style={{ y: orbY }}
        className="pointer-events-none absolute -top-24 -right-24 h-[520px] w-[520px] rounded-full bg-[#8B5CF6]/25 blur-[120px]"
      />
      <motion.div
        style={{ y: orbY2 }}
        className="pointer-events-none absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-[#A855F7]/20 blur-[120px]"
      />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-6 sm:px-10 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#E4E4E7] bg-white px-4 py-1.5 text-xs font-medium text-zinc-600"
          >
            <ShieldCheck className="h-4 w-4 text-[#6C2BD9]" />
            Trusted Loan DSA · Multiple Banks & NBFCs
          </motion.div>

          <h1 className="mt-6 text-5xl font-semibold leading-[1.05] tracking-tighter text-[#09090B] sm:text-6xl md:text-[5rem]">
            <span className="sr-only">Fast Loan Solutions Made Simple.</span>
            <span aria-hidden="true">
              <Line delay={0.15}>Fast Loan</Line>
              <Line delay={0.28}>
                <span className="brand-text">Solutions</span> Made
              </Line>
              <Line delay={0.41}>Simple.</Line>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.6 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-zinc-600 sm:text-lg"
          >
            Personal Loans, Business Loans and Credit Card Assistance through multiple
            Banks and NBFCs — with a team that guides you at every step.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.72 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#apply"
              className="inline-flex items-center gap-2 rounded-full brand-gradient px-8 py-4 font-medium text-white transition-transform hover:scale-105 hover:shadow-[0_8px_24px_rgba(108,43,217,0.3)]"
              data-testid="hero-apply-now"
            >
              Apply Now <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full border border-[#E4E4E7] bg-white px-8 py-4 font-medium text-[#09090B] transition-colors hover:bg-[#F4F4F5]"
              data-testid="hero-contact-us"
            >
              Contact Us
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm text-zinc-500"
          >
            <div className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-[#A855F7] text-[#A855F7]" />
              ))}
              <span className="ml-1 font-medium text-[#09090B]">Trusted by borrowers</span>
            </div>
            <span className="hidden h-4 w-px bg-[#E4E4E7] sm:block" />
            <span>6+ loan products · Nationwide assistance</span>
          </motion.div>
        </div>

        <motion.div
          id="apply"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: EASE, delay: 0.5 }}
          className="lg:col-span-5"
        >
          <LeadForm />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
